import { useState, useEffect, useCallback } from 'react';

/**
 * Breakpoint constants matching Tailwind CSS defaults.
 * Use these instead of hardcoded pixel values throughout the app.
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Debounced matchMedia hook. Listens to a CSS media query
 * and returns whether it currently matches.
 *
 * @example
 * const isWide = useMediaQuery('(min-width: 1024px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Set initial value
    setMatches(mql.matches);

    // Modern browsers
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Returns true when viewport is below the lg breakpoint (< 1024px).
 * This is the unified mobile/desktop split used across the entire app.
 *
 * Replaces all scattered `window.innerWidth < 1024` checks in:
 * - Sidebar.tsx (3 instances)
 * - App.tsx (1 instance)
 * - ContextMenu.tsx (1 instance)
 * - MorningBrief.tsx (2 instances)
 * - Echobot.tsx (1 instance)
 *
 * @example
 * const isMobile = useIsMobile();
 * if (isMobile) { // show mobile layout }
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.lg - 1}px)`);
}

/**
 * Returns true when viewport is at or above the lg breakpoint (>= 1024px).
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}

/**
 * Returns the current breakpoint name based on viewport width.
 *
 * @example
 * const bp = useBreakpoint(); // 'mobile' | 'tablet' | 'desktop' | 'wide'
 */
export function useBreakpoint(): 'mobile' | 'tablet' | 'desktop' | 'wide' {
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
  const isWide = useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`);

  if (isWide) return 'wide';
  if (isDesktop) return 'desktop';
  if (isTablet) return 'tablet';
  return 'mobile';
}

/**
 * Returns true when viewport is at or above the given breakpoint.
 *
 * @example
 * const isAboveMd = useMinWidth('md'); // >= 768px
 */
export function useMinWidth(breakpoint: BreakpointKey): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[breakpoint]}px)`);
}

/**
 * Returns the current window dimensions. Updates on resize with debouncing.
 * Use sparingly – prefer useMediaQuery/useBreakpoint for most cases.
 */
export function useWindowSize(): { width: number; height: number } {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));

  const handleResize = useCallback(() => {
    setSize({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [handleResize]);

  return size;
}
